import { initializeApp } from 'firebase/app'
import { getAuth, connectAuthEmulator } from 'firebase/auth'
import {
  initializeFirestore,
  connectFirestoreEmulator,
  persistentLocalCache,
  persistentMultipleTabManager,
  type FirestoreSettings
} from 'firebase/firestore'
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'
import { getStorage, connectStorageEmulator } from 'firebase/storage'

const firebaseConfig: Record<string, string> = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
}

const measurementId = String(import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || '').trim()
if (measurementId) {
  firebaseConfig.measurementId = measurementId
}

const app = initializeApp(firebaseConfig)

export { app }

const isProdBuild = import.meta.env.PROD === true

// HTTPS beta share (Caddy + cloudflared): emulators on same origin as the page (dev only).
const useEmulatorViaProxy =
  !isProdBuild && import.meta.env.VITE_EMULATOR_VIA_PROXY === 'true'

const isHttpsViaProxy =
  useEmulatorViaProxy &&
  typeof window !== 'undefined' &&
  window.location.protocol === 'https:'

function firestoreEmulatorSettings (): FirestoreSettings {
  const settings: FirestoreSettings = { experimentalForceLongPolling: true }
  // connectFirestoreEmulator() only enables TLS for *.cloudworkstations.dev.
  // trycloudflare (and similar HTTPS proxies) need explicit ssl + host:443.
  if (isHttpsViaProxy) {
    const hostname = window.location.hostname
    const port = window.location.port ? Number(window.location.port) : 443
    settings.host = `${hostname}:${port}`
    settings.ssl = true
  }
  return settings
}

// Web only: the native apps are Swift / Kotlin and use their own SDKs.
const auth = getAuth(app)

export { auth }

// Long polling avoids flaky WebSocket connections to the Firestore emulator from some browsers.
// Production uses a persistent IndexedDB cache so listeners re-attached after a reload or page
// change resume from cache and are only billed for changed docs (browsers without IndexedDB
// fall back to memory automatically).
export const db =
  !isProdBuild && import.meta.env.VITE_USE_FIRESTORE_EMULATOR === 'true'
    ? initializeFirestore(app, firestoreEmulatorSettings())
    : initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() })
    })

// Callable functions are deployed in us-central1 (see firebase deploy output).
export const functions = getFunctions(app, 'us-central1')

// Initialize Firebase Storage (used for sponsor logos, etc.)
export const storage = getStorage(app)

// Optional emulator connections (local dev only).
// Never connect to emulators in a production build: `vite build` sets PROD=true
// even if `.env.production` is missing and `.env` (with emulator flags) was
// merged in — that would ship a broken app (auth + Firestore hit localhost).
const useAuthEmulator =
  !isProdBuild && import.meta.env.VITE_USE_AUTH_EMULATOR === 'true'
const useFirestoreEmulator =
  !isProdBuild && import.meta.env.VITE_USE_FIRESTORE_EMULATOR === 'true'
const useFunctionsEmulator =
  !isProdBuild && import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === 'true'
const useStorageEmulator =
  !isProdBuild && import.meta.env.VITE_USE_STORAGE_EMULATOR === 'true'

const explicitEmulatorHost = import.meta.env.VITE_EMULATOR_HOST
const pageHost =
  typeof window !== 'undefined' && window.location?.hostname
    ? window.location.hostname
    : ''
const isLoopbackHost =
  pageHost === 'localhost' ||
  pageHost === '127.0.0.1' ||
  pageHost === '[::1]'
// 1) If VITE_EMULATOR_HOST is set (.env.emulator.local), always use it — required when
//    the browser must reach emulators via Tailscale/LAN (one host for Auth + Firestore).
// 2) Else loopback URL → emulators on same hostname (matches seed scripts on 127.0.0.1).
// 3) Else use page hostname (e.g. open http://r510:5173 on LAN).
const emulatorHost =
  (explicitEmulatorHost && String(explicitEmulatorHost).trim() !== '')
    ? explicitEmulatorHost
    : isLoopbackHost
      ? pageHost
      : (pageHost || 'localhost')
const authPort = Number(import.meta.env.VITE_AUTH_EMULATOR_PORT || 9099)
const firestorePort = Number(import.meta.env.VITE_FIRESTORE_EMULATOR_PORT || 8081)
const functionsPort = Number(import.meta.env.VITE_FUNCTIONS_EMULATOR_PORT || 5001)
const storagePort = Number(import.meta.env.VITE_STORAGE_EMULATOR_PORT || 9199)

const anyEmulator =
  useAuthEmulator || useFirestoreEmulator || useFunctionsEmulator || useStorageEmulator

if (useEmulatorViaProxy && typeof window !== 'undefined' && anyEmulator) {
  const origin = window.location.origin.replace(/\/$/, '')
  const proxyHost = window.location.hostname
  const proxyPort = window.location.port
    ? Number(window.location.port)
    : window.location.protocol === 'https:'
      ? 443
      : 80

  if (useAuthEmulator) {
    connectAuthEmulator(auth, origin, { disableWarnings: true })
  }
  if (useFirestoreEmulator && !isHttpsViaProxy) {
    connectFirestoreEmulator(db, proxyHost, proxyPort)
  }
  if (useFunctionsEmulator) {
    connectFunctionsEmulator(functions, proxyHost, proxyPort)
    if (isHttpsViaProxy) {
      ;(functions as any).emulatorOrigin = origin
    }
  }
  if (useStorageEmulator) {
    connectStorageEmulator(storage, proxyHost, proxyPort)
    if (isHttpsViaProxy) {
      ;(storage as any)._protocol = 'https'
    }
  }

  if (import.meta.env.DEV) {
    console.info('[NAHA dev] Firebase emulators (HTTPS proxy):', origin, isHttpsViaProxy ? '(TLS)' : '')
  }
} else {
  if (useAuthEmulator) {
    connectAuthEmulator(auth, `http://${emulatorHost}:${authPort}`, { disableWarnings: true })
  }
  if (useFirestoreEmulator) {
    connectFirestoreEmulator(db, emulatorHost, firestorePort)
  }
  if (useFunctionsEmulator) {
    connectFunctionsEmulator(functions, emulatorHost, functionsPort)
  }
  if (useStorageEmulator) {
    connectStorageEmulator(storage, emulatorHost, storagePort)
  }

  if (import.meta.env.DEV && useAuthEmulator) {
    console.info(
      '[NAHA dev] Firebase emulators:',
      `auth=http://${emulatorHost}:${authPort}`,
      `firestore=http://${emulatorHost}:${firestorePort}`,
      `functions=http://${emulatorHost}:${functionsPort}`
    )
  }
}
